import { useState } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { gerarPdfRelatorio, type PdfInput } from "../../utils/pdfExport";
import { buildDrePdfInput } from "../../utils/drePdf";
import { useDrePdfPayload } from "../../contexts/DrePdfContext";
import { ZeusSyncBadge } from "../ui/ZeusSyncBadge";
import { useZeusStatus } from "../../hooks/useZeusStatus";
import { fmtBRL } from "../../utils/formatters";
import { gerarBalancete } from "../../utils/mockData";

// #246: "fluxo" saiu — o Fluxo não exporta PDF (gerava mock desconexo). Só
// DRE Gerencial (v2 real) e DRE Contábil (banner Preview) têm export.
export type PdfScreen = "dre" | "dreContabil";

interface Props {
  breadcrumb: string[];
  pdfScreen?: PdfScreen;
  /** Task #196: refresh in-place da view atual (limpa caches custom + remonta).
   * Async — a TopBar mostra spinner enquanto a promise não resolve. */
  onRefresh?: () => void | Promise<void>;
  /** Task #88: oculta botao "Atualizar" na aba Curva de Mercado da Mesa.
   * F5 resolve o caso de refresh manual; o botao virou ruido visual sem
   * funcao especifica naquela tela. Default false preserva comportamento
   * em todas as outras telas (Fluxo, DRE, demais abas Mesa). */
  hideRefresh?: boolean;
  /** Task #88: oculta botao "Relatorio PDF" na Curva de Mercado. Era
   * placeholder visual (pdfScreen=undefined → desabilitado), Veiga
   * decidiu remover em vez de manter cinza. Default false preserva
   * comportamento. */
  hidePdf?: boolean;
}

/** DRE Contábil ainda em mock (fora do escopo #186; acopla ao #127). A tela
 * tem banner "Preview". O DRE Gerencial usa dado v2 real — ver `baixarPdf` +
 * `buildDrePdfInput`. (Fluxo NÃO exporta mais PDF — #246.) */
function buildMockPdfInput(): PdfInput {
  const today = new Date().toISOString().slice(0, 10);
  const bal = gerarBalancete();
  return {
    filename: `cortex-dre-contabil-${today}`,
    title: "Cortex — DRE Contábil",
    subtitle: "Balancete de verificação por conta contábil",
    filtros: ["Plano de contas: BR"],
    tables: [
      {
        title: "Balancete",
        head: ["Conta", "Saldo inicial", "Débito", "Crédito", "Saldo final"],
        body: bal.map((r) => [
          r.conta,
          fmtBRL(r.saldoInicial),
          fmtBRL(r.debito),
          fmtBRL(r.credito),
          fmtBRL(r.saldoFinal),
        ]),
      },
    ],
  };
}

export function TopBar({ breadcrumb, pdfScreen, onRefresh, hideRefresh, hidePdf }: Props) {
  const [generating, setGenerating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const zeusStatus = useZeusStatus();
  // Task #186: DRE Gerencial real publicado pela FinanceiroDrePage. null
  // enquanto a aba DRE não está montada ou o dado v2 ainda carrega.
  const drePdf = useDrePdfPayload();

  // Só o DRE Gerencial depende de dado v2 carregado; o DRE Contábil usa mock
  // estático e está sempre pronto. (Fluxo não exporta PDF — #246.)
  const pdfReady = pdfScreen !== "dre" || drePdf !== null;

  // Task #196: "Atualizar" = refresh in-place (sem window.location.reload, que
  // resetava a navegação React pro tab default). onRefresh (App) limpa os caches
  // custom + remonta a view; o spinner fica enquanto a promise não resolve.
  async function handleAtualizar() {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setIsRefreshing(false);
    }
  }

  async function baixarPdf() {
    if (!pdfScreen) return;
    let input: PdfInput;
    if (pdfScreen === "dre") {
      if (!drePdf) return; // dado v2 ainda não disponível.
      input = buildDrePdfInput(drePdf.data, drePdf.regime, drePdf.mesRef);
    } else {
      input = buildMockPdfInput(); // dreContabil (mock, banner Preview na tela)
    }
    setGenerating(true);
    try {
      await gerarPdfRelatorio(input);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-y-2 mb-6">
      <div className="flex items-center gap-2 text-sm">
        {breadcrumb.map((part, i) => (
          <div key={i} className="flex items-center gap-2">
            <span
              className={
                i === breadcrumb.length - 1 ? "text-white font-semibold" : "text-white/50"
              }
            >
              {part}
            </span>
            {i < breadcrumb.length - 1 && <span className="text-white/30">/</span>}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        {/* Task #63: badge "ultima integracao Zeus" — esquerda dos botoes
            no canto superior direito; tooltip com detalhes ao hover. */}
        <ZeusSyncBadge data={zeusStatus.data} isLoading={zeusStatus.isLoading} />
        {!hideRefresh && (
          <button
            type="button"
            onClick={handleAtualizar}
            disabled={isRefreshing}
            data-testid="topbar-atualizar"
            className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 border border-white/10 rounded-md hover:bg-white/5 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} />
            {isRefreshing ? "Atualizando…" : "Atualizar"}
          </button>
        )}
        {/* Task #196: PDF só renderiza nas telas COM export (pdfScreen definido)
            — Fluxo, DRE Gerencial v2 (#186) e DRE Contábil. Em Despesas/Indicadores/
            Orçado/Mesa (pdfScreen=undefined) some, em vez de ficar cinza/inútil. */}
        {!hidePdf && pdfScreen && (
          <button
            type="button"
            onClick={baixarPdf}
            disabled={!pdfReady || generating}
            title={
              pdfScreen === "dre" && !pdfReady ? "Carregando DRE…" : undefined
            }
            data-testid="topbar-pdf"
            className="flex items-center gap-2 px-3 py-1.5 text-xs text-indra-azul bg-indra-turquesa rounded-md font-semibold hover:bg-indra-turquesa/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileText size={14} />
            {generating ? "Gerando…" : "Relatório PDF"}
          </button>
        )}
      </div>
    </div>
  );
}
